// ui/src/components/rep/RepPayoutExplanation.jsx
/**
 * @file RepPayoutExplanation.jsx
 * @module ui/src/components/rep/RepPayoutExplanation
 * @description Full-page payout detail for the rep persona.
 *
 * S805 REWRITE: Switched from EP 51 (raw engine artifacts) to EP 12.2
 * (GET /api/v1/payout-summaries/{payout_summary_id}) — same endpoint used
 * by manager My Payout. Rendering is an exact full-page replica of the
 * manager My Payout screen (Image 5 gold standard).
 *
 * Props:
 *   payoutSummaryId {string} — lifted to RepShell level from EP 12.1 (S805).
 *   positionId      {string} — still passed for future use / audit context.
 *   period          {object} — selectedPeriod object from RepShell.
 *
 * EP 12.2 response shape:
 *   total_payout, currency_code, is_latest_final, calculated_at,
 *   quota_reporting, credit_total_reporting, attainment_pct,
 *   components[]: { component_type, resolved_payout, attainment_pct,
 *                   quota, credit, ramp_factor, gate_name, gate_met }
 *   calculation_trace: { ote_midpoint, variable_pct, rate_band_applied,
 *                        ramp_month, ramp_factor, proof_steps[] }
 *   planContext: { planId, planCode, planName, planVersion, planVersionLabel,
 *                  effectiveFrom, effectiveTo }
 *
 * Rendering rules (locked — manager gold standard):
 *   Summary: Total Payout, Attainment, Quota, Credit, OTE, Rate Band — ALL SIX always shown.
 *   is_result rows: primary-color text on primary-tinted bg.
 *   is_total  rows: solid primary bg + white text.
 *   is_negative rows: danger color on value.
 *   Component breakdown: Quota + Credit + Attainment always shown per component.
 *
 * @locked-rules
 *   AH-RBAC-PAYOUT: EP 12.2 allows rep own payout_summary_id (S803 fix confirmed).
 *   NEVER use EP 51 for this page (mastercontext S804/S805 — locked decision).
 */

import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatCurrency(value, currencyCode) {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode || 'USD',
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return '—';
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)}%`;
}

// ---------------------------------------------------------------------------
// usePayoutDetail — fetches EP 12.2 for this payout_summary_id
// ---------------------------------------------------------------------------

function usePayoutDetail(payoutSummaryId) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (!payoutSummaryId) { setDetail(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);

    fetch(`/api/v1/payout-summaries/${encodeURIComponent(payoutSummaryId)}`, {
      credentials: 'include',
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => { if (!cancelled) setDetail(data); })
      .catch((e) => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [payoutSummaryId]);

  return { detail, loading, error };
}

// ---------------------------------------------------------------------------
// ComponentRow — expandable component in breakdown
// Shows Quota + Credit + Attainment always (per S805 requirement)
// ---------------------------------------------------------------------------

function ComponentRow({ component, currency }) {
  const [expanded, setExpanded] = useState(false);

  const attPct  = component.attainment_pct != null ? Number(component.attainment_pct) * 100 : null;
  const hasRamp = component.ramp_factor && Number(component.ramp_factor) < 1;

  return (
    <div className="rpe__component">
      <div className="rpe__component-header">
        <div className="rpe__component-name-row">
          <span className="rpe__component-name">{component.component_type?.toUpperCase()}</span>
          {hasRamp ? (
            <span className="rpe__ramp-badge">
              Ramped {(Number(component.ramp_factor) * 100).toFixed(0)}%
            </span>
          ) : null}
        </div>
        <div className="rpe__component-actions">
          <span className="rpe__component-payout">
            {formatCurrency(component.component_payout ?? component.resolved_payout, currency)}
          </span>
          <button
            type="button"
            className="icon-button rpe__component-toggle"
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${component.component_type} details`}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? '−' : '+'}
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="rpe__component-detail">
          <div className="rpe__component-metrics">
            {/* Quota — always shown (S805: explicit even when derivable) */}
            <div>
              <span>Quota</span>
              <strong>{formatCurrency(component.quota, currency)}</strong>
            </div>
            {/* Credit — always shown */}
            <div>
              <span>Credit</span>
              <strong>{formatCurrency(component.credit, currency)}</strong>
            </div>
            {/* Attainment — always shown */}
            <div>
              <span>Attainment</span>
              <strong>{formatPercent(attPct)}</strong>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

ComponentRow.propTypes = {
  component: PropTypes.object.isRequired,
  currency:  PropTypes.string.isRequired,
};

// ---------------------------------------------------------------------------
// RepPayoutExplanation — full-page replica of manager My Payout (Image 5)
// ---------------------------------------------------------------------------

function RepPayoutExplanation({ payoutSummaryId, positionId, period }) {
  const { detail, loading, error } = usePayoutDetail(payoutSummaryId);

  // Group proof steps by section — same as manager rendering
  const trace      = detail?.calculation_trace ?? {};
  const proofSteps = trace.proof_steps ?? [];
  const components = detail?.components ?? [];
  const plan       = detail?.planContext ?? null;
  const currency   = detail?.currency_code || 'USD';

  // ALL hooks before any conditional returns (AH-HOOKS)
  const sections = useMemo(() => {
    const map  = new Map();
    const list = [];
    for (const step of proofSteps) {
      const sec = step.section || 'Other';
      if (!map.has(sec)) { map.set(sec, []); list.push({ section: sec, steps: map.get(sec) }); }
      map.get(sec).push(step);
    }
    return list;
  }, [proofSteps]);

  // Component aggregation — GROUP BY component_type, SUM resolved_payout.
  // Engine writes one payout_detail row per revenue-type per band → multiple
  // raw rows with the same component_type. S893 fix applied to PayoutResultsViewer
  // (admin side); same fix required here for rep-facing payout detail.
  // Use highest resolved_payout row for metadata (quota, credit, attainment_pct).
  const aggregatedComponents = useMemo(() => {
    const map = new Map();
    for (const c of components) {
      const key = c.component_type;
      if (!map.has(key)) {
        map.set(key, { ...c, resolved_payout: Number(c.resolved_payout ?? 0) });
      } else {
        const existing = map.get(key);
        const incoming = Number(c.resolved_payout ?? 0);
        existing.resolved_payout += incoming;
        // Keep metadata from highest-payout row (quota, credit, attainment_pct)
        if (incoming > Number(existing._best_payout ?? 0)) {
          existing._best_payout   = incoming;
          existing.quota          = c.quota;
          existing.credit         = c.credit;
          existing.attainment_pct = c.attainment_pct;
          existing.ramp_factor    = c.ramp_factor;
        }
      }
    }
    return [...map.values()];
  }, [components]);

  // Rate band label
  // Guard: from_pct=0 AND to_pct=0 is a placeholder written by the engine when
  // the specific band boundary is not stored on the proof step — treat as "Yes"
  // (band was applied) rather than displaying the misleading "0% – 0%" string.
  const rateBandLabel = (() => {
    const band = trace.rate_band_applied;
    if (!band) return '—';
    if (
      band.from_pct != null && band.to_pct != null &&
      !(Number(band.from_pct) === 0 && Number(band.to_pct) === 0)
    ) {
      return `${(Number(band.from_pct) * 100).toFixed(0)}% – ${(Number(band.to_pct) * 100).toFixed(0)}%`;
    }
    return 'Yes';
  })();

  if (!payoutSummaryId) {
    return (
      <div className="rpe__empty">
        No payout selected. Select a period with payout data.
        <style>{styles}</style>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rpe__loading" aria-busy="true">
        <div className="skeleton rpe__skeleton--tall" />
        <div className="skeleton rpe__skeleton" />
        <div className="skeleton rpe__skeleton" />
        <div className="skeleton rpe__skeleton" />
        <style>{styles}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rpe__error" role="alert">
        <h3>Unable to load payout detail</h3>
        <p>{error.message}</p>
        <style>{styles}</style>
      </div>
    );
  }

  if (!detail) return null;

  // Derive attainment from first component if not at summary level
  const attainmentPct = detail.attainment_pct != null
    ? Number(detail.attainment_pct) * 100
    : (components[0]?.attainment_pct != null ? Number(components[0].attainment_pct) * 100 : null);

  const runBadgeLabel = detail.is_latest_final ? 'Final' : 'Provisional';

  return (
    <section className="rpe" aria-label="My Payout">
      {/* ── Page header ── */}
      <header className="rpe__page-header">
        <div>
          <p className="rpe__eyebrow">My Payout</p>
          {period?.period_label ? (
            <p className="rpe__period-label">{period.period_label}</p>
          ) : null}
        </div>
        <span className={`rpe__run-badge rpe__run-badge--${detail.is_latest_final ? 'final' : 'provisional'}`}>
          {runBadgeLabel}
        </span>
      </header>

      {/* ── Summary metrics — 2-row narrative ── */}
      <div className="rpe__summary-card">
        {/* Row 1: what happened — Quota → Credit → Attainment */}
        <div className="rpe__summary-row rpe__summary-row--3">
          <div className="rpe__summary-metric">
            <span>Quota</span>
            <strong>{formatCurrency(detail.quota_reporting ?? components[0]?.quota, currency)}</strong>
          </div>
          <div className="rpe__summary-metric">
            <span>Credit</span>
            <strong>{formatCurrency(detail.credit_total_reporting ?? components[0]?.credit, currency)}</strong>
          </div>
          <div className="rpe__summary-metric">
            <span>Attainment</span>
            <strong>{formatPercent(attainmentPct)}</strong>
          </div>
        </div>

        {/* Row 2: how it was priced → result — OTE → Variable % → Rate Band → Total Payout */}
        <div className="rpe__summary-row rpe__summary-row--4">
          <div className="rpe__summary-metric">
            <span>OTE</span>
            <strong>{formatCurrency(trace.ote_midpoint, currency)}</strong>
          </div>
          <div className="rpe__summary-metric">
            <span>Variable %</span>
            <strong>{trace.variable_pct != null ? `${(Number(trace.variable_pct) * 100).toFixed(0)}%` : '—'}</strong>
          </div>
          <div className="rpe__summary-metric">
            <span>Rate Band</span>
            <strong>{rateBandLabel}</strong>
          </div>
          <div className="rpe__summary-metric rpe__summary-metric--total">
            <span>Total Payout</span>
            <strong>{formatCurrency(detail.total_payout, currency)}</strong>
          </div>
        </div>
      </div>

      {/* ── Governing Plan ── */}
      {plan ? (
        <section className="rpe__section">
          <p className="rpe__section-eyebrow">Governing Plan</p>
          <div className="rpe__plan-row">
            <div>
              <p className="rpe__plan-name">{plan.planName || plan.planCode}</p>
              <p className="rpe__plan-code">{plan.planCode}</p>
              {plan.effectiveFrom ? (
                <p className="rpe__plan-governs">
                  ● Governs current period · from {plan.effectiveFrom}
                </p>
              ) : null}
            </div>
            {plan.planVersionLabel ? (
              <span className="rpe__version-pill">{plan.planVersionLabel}</span>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* ── Component Breakdown — only when 2+ aggregated component types.
           aggregatedComponents groups raw payout_detail rows by component_type,
           summing resolved_payout and using the highest-payout row for metadata.
           With 1 aggregated component the proof steps tell the full story. ── */}
      {aggregatedComponents.length > 1 ? (
        <section className="rpe__section">
          <p className="rpe__section-eyebrow">Component Breakdown</p>
          {aggregatedComponents.map((c, i) => (
            <ComponentRow key={c.component_type ?? i} component={c} currency={currency} />
          ))}
        </section>
      ) : null}

      {/* ── How It Was Calculated (proof steps) ── */}
      {proofSteps.length > 0 ? (
        <section className="rpe__section">
          <p className="rpe__section-eyebrow">How It Was Calculated</p>
          <div className="rpe__proof">
            {sections.map(({ section, steps }) => (
              <div key={section}>
                {section !== 'TOTAL' && section !== 'Status' ? (
                  <p className="rpe__proof-section">{section}</p>
                ) : null}
                {steps.map((step, si) => {
                  const isTotal  = step.is_total;
                  const isResult = step.is_result;
                  const isNeg    = step.is_negative;

                  const rowCls = [
                    'rpe__proof-row',
                    isTotal  ? 'rpe__proof-row--total'  : '',
                    isResult ? 'rpe__proof-row--result' : '',
                    isNeg    ? 'rpe__proof-row--neg'    : '',
                  ].filter(Boolean).join(' ');

                  return (
                    <div key={si} className={rowCls}>
                      <span className="rpe__proof-label">
                        {step.label}
                        {step.note ? (
                          <span className="rpe__proof-note"> {step.note}</span>
                        ) : null}
                      </span>
                      <span className="rpe__proof-value">{step.value}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <style>{styles}</style>
    </section>
  );
}

RepPayoutExplanation.propTypes = {
  payoutSummaryId: PropTypes.string,
  positionId:      PropTypes.string.isRequired,
  period:          PropTypes.object,
};

// defaultProps replaced by default params per React 18.3+
RepPayoutExplanation.defaultProps = undefined;

// ---------------------------------------------------------------------------
// Styles — full-page layout matching manager My Payout
// ---------------------------------------------------------------------------

const styles = `
  .rpe {
    display: flex;
    flex-direction: column;
    gap: 14px;
    color: rgb(var(--color-text));

  }

  /* ── Page header ── */
  .rpe__page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .rpe__eyebrow {
    margin: 0 0 2px;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .rpe__period-label {
    margin: 0;
    font-size: 13px;
    color: rgb(var(--color-text-muted));
  }

  .rpe__run-badge {
    padding: 2px 10px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }

  .rpe__run-badge--final {
    border-color: rgb(var(--color-primary) / 0.25);
    background: rgb(var(--color-primary) / 0.06);
    color: rgb(var(--color-primary));
  }

  .rpe__run-badge--provisional {
    border-color: rgb(var(--color-warning) / 0.3);
    background: rgb(var(--color-warning) / 0.07);
    color: rgb(var(--color-warning));
  }

  /* ── Summary metrics — 2-row narrative grid ──
     Row 1 (3 cells): Quota · Credit · Attainment
     Row 2 (4 cells): OTE · Variable % · Rate Band · Total Payout
  ── */
  .rpe__summary-card {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  /* Row 2 spans 4 cols — achieved by making the whole card 12-col and
     row 1 cells span 4 each, row 2 cells span 3 each. Simpler: use subgrid
     trick by wrapping row 2 separately. Instead: use auto-flow with explicit
     widths via a 12-col base. Simplest correct approach: two separate grids. */
  .rpe__summary-card {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .rpe__summary-row {
    display: grid;
    gap: 8px;
  }

  .rpe__summary-row--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .rpe__summary-row--4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }

  .rpe__summary-metric {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 14px 16px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 10px;
    background: rgb(var(--color-surface));
  }

  .rpe__summary-metric span {
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .rpe__summary-metric strong {
    font-size: 18px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  /* Total Payout — conclusion cell, primary tinted */
  .rpe__summary-metric--total {
    border-color: rgb(var(--color-primary) / 0.25);
    background: rgb(var(--color-primary) / 0.05);
  }

  .rpe__summary-metric--total strong {
    color: rgb(var(--color-primary));
    font-size: 20px;
  }

  /* ── Sections ── */
  .rpe__section {
    padding: 14px 16px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 12px;
    background: rgb(var(--color-surface));
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .rpe__section-eyebrow {
    margin: 0;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  /* ── Governing Plan ── */
  .rpe__plan-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .rpe__plan-name { margin: 0; font-size: 15px; font-weight: 600; }
  .rpe__plan-code { margin: 2px 0 0; font-size: 11px; color: rgb(var(--color-text-muted)); }
  .rpe__plan-governs { margin: 4px 0 0; font-size: 11px; color: rgb(var(--color-success)); }

  .rpe__version-pill {
    padding: 2px 9px;
    border: 1px solid rgb(var(--color-primary) / 0.25);
    border-radius: 999px;
    background: rgb(var(--color-primary) / 0.06);
    color: rgb(var(--color-primary));
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }

  /* ── Component breakdown ── */
  .rpe__component {
    border: 1px solid rgb(var(--color-border));
    border-radius: 10px;
    overflow: hidden;
  }

  .rpe__component-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    gap: 8px;
  }

  .rpe__component-name-row { display: flex; align-items: center; gap: 8px; }
  .rpe__component-name { font-size: 13px; font-weight: 600; }

  .rpe__ramp-badge {
    padding: 1px 7px;
    border: 1px solid rgb(var(--color-warning) / 0.3);
    border-radius: 999px;
    background: rgb(var(--color-warning) / 0.07);
    color: rgb(var(--color-warning));
    font-size: 10px;
    font-weight: 600;
  }

  .rpe__component-actions { display: flex; align-items: center; gap: 8px; }
  .rpe__component-payout { font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums; }
  .rpe__component-toggle { font-size: 14px; }

  .rpe__component-detail {
    padding: 10px 14px;
    border-top: 1px solid rgb(var(--color-border));
    background: rgb(var(--color-surface-muted));
  }

  .rpe__component-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }

  .rpe__component-metrics div { display: flex; flex-direction: column; gap: 3px; }
  .rpe__component-metrics span { font-size: 10px; color: rgb(var(--color-text-muted)); font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
  .rpe__component-metrics strong { font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums; }

  /* ── Proof steps ── */
  .rpe__proof { display: flex; flex-direction: column; gap: 2px; }

  .rpe__proof-section {
    margin: 8px 0 2px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: rgb(var(--color-primary));
    opacity: 0.8;
  }

  .rpe__proof-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 13px;
    color: rgb(var(--color-text-muted));
  }

  /* is_result — primary tint bg + primary text */
  .rpe__proof-row--result {
    background: rgb(var(--color-primary) / 0.08);
    color: rgb(var(--color-primary));
    font-weight: 600;
  }

  /* is_total — solid primary bg + white text */
  .rpe__proof-row--total {
    background: rgb(var(--color-primary));
    color: white;
    font-weight: 700;
  }

  /* is_negative — danger color on value */
  .rpe__proof-row--neg .rpe__proof-value { color: rgb(var(--color-danger)); }

  .rpe__proof-label { min-width: 0; flex: 1; }
  .rpe__proof-note { font-size: 11px; opacity: 0.7; font-weight: 400; margin-left: 6px; }
  .rpe__proof-value { white-space: nowrap; font-variant-numeric: tabular-nums; font-weight: 500; }

  /* ── Loading / error / empty ── */
  .rpe__loading { display: flex; flex-direction: column; gap: 10px; }
  .rpe__skeleton--tall { height: 120px; border-radius: 12px; }
  .rpe__skeleton { height: 80px; border-radius: 12px; }

  .rpe__error {
    padding: 16px;
    border: 1px solid rgb(var(--color-danger) / 0.2);
    border-radius: 10px;
    background: rgb(var(--color-danger) / 0.05);
  }

  .rpe__error h3 { margin: 0 0 6px; font-size: 13px; }
  .rpe__error p  { margin: 0; font-size: 12px; color: rgb(var(--color-text-muted)); }

  .rpe__empty {
    padding: 32px 16px;
    border: 1px dashed rgb(var(--color-border));
    border-radius: 10px;
    color: rgb(var(--color-text-muted));
    font-size: 13px;
    text-align: center;
  }

  @media (max-width: 700px) {
    .rpe__summary-row--3 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .rpe__summary-row--4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .rpe__component-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
`;

export default RepPayoutExplanation;
